/**
 * The ⌘K command palette (§13.2): "jump to card, search transactions, 'Sync
 * now', 'Upload statement', 'Add cashback entry', 'Plan a purchase'."
 *
 * Every entry here is something the app can already do from a page — the
 * palette is a faster route to it, never the only one. Typing anything also
 * offers "search transactions for …", which lands on the Transactions page with
 * the search filled in (`?q=`), so a merchant name is one keystroke sequence
 * away from every row it appears on.
 */

import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import {
  ArrowLeftRight,
  ArrowRight,
  Calculator,
  CalendarClock,
  Coins,
  CreditCard,
  FileText,
  Hammer,
  LayoutDashboard,
  Moon,
  Plus,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Sun,
  Upload,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { cardsApi, type Card } from "@/lib/api/client";
import { useCommands } from "@/lib/commands";
import { formatLast4 } from "@/lib/format";
import { useTheme } from "@/lib/useTheme";

/** Every page, for the palette and the phone's More screen alike. */
export const PAGES = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, keywords: ["home", "overview"] },
  { to: "/cards", label: "Cards", icon: CreditCard, keywords: ["wallet", "deck"] },
  { to: "/cycles", label: "Billing cycles", icon: CalendarClock, keywords: ["due", "close", "statement date"] },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, keywords: ["spend", "purchases"] },
  { to: "/cashback", label: "Cashback", icon: Coins, keywords: ["rewards", "earned"] },
  { to: "/statements", label: "Statements", icon: FileText, keywords: ["review", "pdf", "bills"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, keywords: ["email", "rules", "inbox"] },
  { to: "/status", label: "Build status", icon: Hammer, keywords: ["progress"] },
];

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { openPlanner, syncNow } = useCommands();
  const { theme, toggle } = useTheme();
  const [search, setSearch] = React.useState("");

  const { data: cards } = useQuery({
    queryKey: ["cards", false],
    queryFn: () => cardsApi.list(false),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const run = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };

  const go = (to: string) => run(() => navigate(to));
  const query = search.trim();

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      overlayClassName="dialog-overlay"
      contentClassName="dialog-content fixed left-1/2 top-[12vh] z-50 w-[min(40rem,94vw)] -translate-x-1/2 overflow-hidden"
      className="cmdk"
    >
      <div className="flex items-center gap-3 border-b border-hairline px-4">
        <Search className="h-4 w-4 shrink-0 text-faint" aria-hidden />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Search cards, pages, actions — or type a merchant…"
          className="h-14 w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-faint focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <kbd className="hidden shrink-0 rounded-md border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-faint sm:block">
          ESC
        </kbd>
      </div>

      <Command.List className="max-h-[min(60vh,28rem)] overflow-y-auto overscroll-contain p-2">
        <Command.Empty className="px-3 py-10 text-center text-sm text-muted">
          Nothing matches “{query}”.
        </Command.Empty>

        {query && (
          <Command.Group heading="Search">
            <Item
              value={`search-transactions ${query}`}
              icon={ArrowLeftRight}
              onSelect={go(`/transactions?q=${encodeURIComponent(query)}`)}
              hint="Transactions"
            >
              Search transactions for “{query}”
            </Item>
          </Command.Group>
        )}

        <Command.Group heading="Actions">
          <Item value="plan a purchase which card" icon={Calculator} onSelect={run(openPlanner)} accent>
            Plan a purchase
          </Item>
          <Item value="sync inbox now email" icon={RefreshCw} onSelect={run(syncNow)}>
            Sync inbox now
          </Item>
          <Item value="upload statement pdf" icon={Upload} onSelect={go("/statements")}>
            Upload a statement
          </Item>
          <Item value="add cashback entry manual" icon={Plus} onSelect={go("/cashback")}>
            Add a cashback entry
          </Item>
          <Item value="add card new" icon={CreditCard} onSelect={go("/cards")}>
            Add a card
          </Item>
          <Item
            value={`theme ${theme === "dark" ? "light" : "dark"} mode toggle`}
            icon={theme === "dark" ? Sun : Moon}
            onSelect={run(toggle)}
          >
            Switch to {theme === "dark" ? "light" : "dark"} theme
          </Item>
        </Command.Group>

        {cards && cards.length > 0 && (
          <Command.Group heading="Cards">
            {cards.map((c) => (
              <CardItem key={c.id} card={c} onSelect={go(`/cards/${c.id}`)} />
            ))}
          </Command.Group>
        )}

        <Command.Group heading="Go to">
          {PAGES.map((p) => (
            <Item
              key={p.to}
              value={`go ${p.label} ${p.keywords.join(" ")}`}
              icon={p.icon}
              onSelect={go(p.to)}
            >
              {p.label}
            </Item>
          ))}
        </Command.Group>
      </Command.List>

      <div className="flex items-center gap-4 border-t border-hairline px-4 py-2.5 font-mono text-[10.5px] text-faint">
        <span>↑↓ to move</span>
        <span>↵ to open</span>
        <span className="ml-auto">MyDeck</span>
      </div>
    </Command.Dialog>
  );
}

function Item({
  value,
  icon: Icon,
  onSelect,
  children,
  hint,
  accent,
}: {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  onSelect: () => void;
  children: React.ReactNode;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <Command.Item value={value} onSelect={onSelect} className="cmdk-item group">
      <span
        className={
          accent
            ? "grid h-7 w-7 place-items-center rounded-lg border border-brass/25 bg-brass/10 text-brass"
            : "grid h-7 w-7 place-items-center rounded-lg border border-hairline bg-raised text-muted group-data-[selected=true]:text-ink"
        }
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint && <span className="text-xs text-faint">{hint}</span>}
      <ArrowRight
        className="h-3.5 w-3.5 -translate-x-1 text-faint opacity-0 transition-all duration-300 group-data-[selected=true]:translate-x-0 group-data-[selected=true]:opacity-100"
        aria-hidden
      />
    </Command.Item>
  );
}

function CardItem({ card, onSelect }: { card: Card; onSelect: () => void }) {
  const theme = card.theme;
  const name = card.nickname || card.product_name || card.issuer;
  return (
    <Command.Item
      value={`card ${name} ${card.issuer} ${card.product_name ?? ""} ${card.last4}`}
      onSelect={onSelect}
      className="cmdk-item group"
    >
      <span
        aria-hidden
        className="h-[18px] w-7 shrink-0 rounded-[4px] shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]"
        style={{
          backgroundImage: theme
            ? `linear-gradient(140deg, ${theme.gradient_from}, ${theme.gradient_to})`
            : undefined,
        }}
      />
      <span className="min-w-0 flex-1 truncate">
        {name}
        <span className="ml-2 text-faint">{card.issuer}</span>
      </span>
      <span className="font-mono text-xs text-faint">{formatLast4(card.last4)}</span>
    </Command.Item>
  );
}
